import type { ThemeClasses } from "../types";
import { useIsReseller } from "../../../../../../hooks/useIsReseller";
import useIsMonitor from "../../../../../../hooks/useIsMonitor";
import { DeviceFilters } from "./DeviceFilters";
import { DeviceViewToggle } from "./DeviceViewToggle";
import { DeviceBarridoButton } from "./DeviceBarridoButton";
import { useDisableCRUD } from "../../../../../../store/admin/companiesStore";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CRUDTooltip } from "../../../../../../components/base/CRUDTooltip";

interface DeviceHeaderProps {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  selectedStatus: string;
  selectedType: string;
  searchQuery: string;
  selectedView: "grid" | "list";
  onAddDevice: () => void;
  onStatusChange: (value: string | null) => void;
  onTypeChange: (value: string | null) => void;
  onSearchChange: (value: string) => void;
  setSelectedView: (view: "grid" | "list") => void;
  t: (key: string, options?: any) => string;
  // Multi-selection props
  isSelectionMode?: boolean;
  onToggleSelectionMode?: () => void;
  onSelectAll?: () => void;
}

export function DeviceHeader({
  themeClasses,
  isDarkMode,
  selectedStatus,
  selectedType,
  searchQuery,
  selectedView,
  onAddDevice,
  onStatusChange,
  onTypeChange,
  onSearchChange,
  setSelectedView,
  isSelectionMode = false,
  onToggleSelectionMode,
  onSelectAll,
}: DeviceHeaderProps) {
  const { t } = useTranslation("adminSections");
  const { isReseller } = useIsReseller();
  const { isMonitor, isReadOnly } = useIsMonitor();
  const disableCRUD = useDisableCRUD();
  const location = useLocation();

  // En reseller-admin siempre se puede agregar dispositivos
  // En admin solo si es reseller y no es monitorista
  const isResellerAdmin = location.pathname.startsWith("/reseller-admin");
  const canAddDevice =
    isResellerAdmin || (isReseller && !isMonitor && !isReadOnly);

  const isCentralGPS = import.meta.env.VITE_THEME === "central-gps";

  return (
    <div
      className={`${themeClasses.bg} border-b ${themeClasses.border} px-4 py-3 sm:px-6 sm:py-4`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-3">
        <div className="min-w-0">
          <h1 className={`text-xl sm:text-2xl font-bold ${themeClasses.text}`}>
            {t("devices.title")}
          </h1>
          <p className={`text-sm ${themeClasses.textSecondary}`}>
            {t("devices.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto flex-shrink-0">
          {isResellerAdmin && (
            <DeviceBarridoButton
              themeClasses={themeClasses}
              isDarkMode={isDarkMode}
              t={t}
            />
          )}
          {isCentralGPS && (
            <CRUDTooltip className="flex-shrink-0">
              <button
                onClick={onAddDevice}
                disabled={disableCRUD}
                className="bg-primary-500 hover:bg-primary-400 text-gray-900 px-3 sm:px-4 py-2 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors whitespace-nowrap text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="ri-add-line"></i>
                <span className="hidden xs:inline">
                  {t("devices.actions.add")}
                </span>
                <span className="xs:hidden">Nuevo</span>
              </button>
            </CRUDTooltip>
          )}
          {canAddDevice && !isCentralGPS && (
            <CRUDTooltip className="flex-shrink-0">
              <button
                onClick={onAddDevice}
                disabled={disableCRUD}
                className="bg-primary-500 hover:bg-primary-400 text-gray-900 px-3 sm:px-4 py-2 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors whitespace-nowrap text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="ri-add-line"></i>
                <span className="hidden xs:inline">
                  {t("devices.actions.add")}
                </span>
                <span className="xs:hidden">Nuevo</span>
              </button>
            </CRUDTooltip>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 min-w-0">
        {/* Filters */}
        <DeviceFilters
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          selectedStatus={selectedStatus}
          selectedType={selectedType}
          searchQuery={searchQuery}
          onStatusChange={onStatusChange}
          onTypeChange={onTypeChange}
          onSearchChange={onSearchChange}
          t={t}
        />

        {/* Selection Mode and View Toggle */}
        <div className="ml-auto flex items-center gap-2 shrink-0">
          {/* Selection Mode Toggle */}
          {onToggleSelectionMode && (
            <button
              onClick={onToggleSelectionMode}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                isSelectionMode
                  ? "bg-primary-500 text-gray-900"
                  : `${themeClasses.buttonHover} border ${themeClasses.cardBorder} ${themeClasses.buttonText}`
              }`}
            >
              <i
                className={`${isSelectionMode ? "ri-checkbox-multiple-fill" : "ri-checkbox-multiple-line"}`}
              ></i>
              <span className="hidden sm:inline">
                {isSelectionMode
                  ? t("devices.exitSelection")
                  : t("devices.selectMultiple")}
              </span>
            </button>
          )}

          {/* Select All Button (only in selection mode) */}
          {isSelectionMode && onSelectAll && (
            <button
              onClick={onSelectAll}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${themeClasses.buttonHover} border ${themeClasses.cardBorder} ${themeClasses.buttonText}`}
            >
              <i className="ri-checkbox-fill mr-1"></i>
              <span className="hidden sm:inline">{t("devices.selectAll")}</span>
            </button>
          )}

          <DeviceViewToggle
            themeClasses={themeClasses}
            selectedView={selectedView}
            onViewChange={setSelectedView}
          />
        </div>
      </div>
    </div>
  );
}
